import { useEffect, useState } from "react";
import { selectSuppliersLoadingPag, selectSuppliersPag } from "../../redux/suppliers/selectors";
import { AdminTable } from "./AdminTable";
import { useDispatch, useSelector } from "react-redux";
import { Column } from "devextreme-react/data-grid";
import { createSupplier, deleteSupplier, editSupplier, getSuppliersPag, toggleActiveSupplier } from "../../redux/suppliers/thunks";
import { openModal } from "../../redux/modal/modalSlice";
import { useTranslation } from "react-i18next";
import CustomSwitch from "../common/CustomSwitch";
import SupplierServicesDetail from "./SupplierServicesDetail";

const initialSortOptions = {
    columna: "supplierId",
    sortOrderAsc: true
}

function SupplierManagement() {
    const { t } = useTranslation();
    const suppliers = useSelector(selectSuppliersPag);
    const dispatch = useDispatch();
    const loading = useSelector(selectSuppliersLoadingPag);
    const [sortOptions, setSortOptions] = useState(initialSortOptions);    

    useEffect(()=>{
        getListSuppliers(mapPageInfo(suppliers));
    }, [])

    function mapPageInfo(suppliersData){
        return {
            pageIndex: suppliersData.pageIndex ?? 0,
            pageSize: suppliersData.pageSize ?? 10,
            orderByField:  suppliersData.columna ?? "supplierId",
            orderByAscending:  suppliersData.sortOrderAsc ?? true
        }
    }
      
    const getListSuppliers = (pageInfo) => {
        let query = `?pageIndex=${pageInfo.pageIndex}&pageNum=${pageInfo.pageSize}`
        if(pageInfo.searchString?.trim().length > 0){
            query = query.concat(`&searchString=${pageInfo.searchString.trim()}`)
        }
        if(pageInfo.orderByField)
        {
            query = query.concat(`&orderByField=${pageInfo.orderByField}&orderByAscending=${pageInfo.orderByAscending}`)
        }
        dispatch(getSuppliersPag({query}));
    }

    const handleToggleActive = (e, rowData) => {
        e.stopPropagation();
        dispatch(toggleActiveSupplier({ param: rowData.supplierId })).then((data) => {
            if(data.error)
                dispatch(openModal({ type: "warning", message: data.error.message }))
            else
                getListSuppliers(mapPageInfo({...suppliers, ...sortOptions}));
        });
    };

    const columnsSuppliers = [
        <Column sortIndex={1} key={"supplier-1"} dataField="supplierId" caption={t("Code")} allowEditing={false} allowCreating={false}/>, 
        <Column key={"supplier-2"} dataField="supplierCode" caption={t("SupplierCode")} dataType="string" />, 
        <Column key={"supplier-3"} dataField="description" caption={t("Description")} dataType="string" />, 
        <Column key={"supplier-4"} dataField="cnpj" caption={t("CNPJ")} dataType="string" />, 
        <Column key={"supplier-5"} dataField="contactName" caption={t("ContactName")} dataType="string" />, 
        <Column key={"supplier-6"} dataField="email" caption={t("Email")} dataType="string" />, 
        <Column key={"supplier-7"} dataField="phone" caption={t("Phone")} dataType="string" />, 
        <Column 
            key={"supplier-8"} 
            dataField="active" 
            caption={t("Status")} 
            width={100}
            allowEditing={false}
            allowCreating={false}
            alignment="center"
            cellRender={(cellData) => (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }} onClick={(e) => e.stopPropagation()}>
                    <CustomSwitch
                        checked={Boolean(cellData.data.active)}
                        onChange={(e) => handleToggleActive(e, cellData.data)}
                    />
                </div>
            )}
        />
    ] 

    function handleSaveNewSupplierClick(value, closeCreateModal) {   
        const description = value.description?.trim();    
        if(description){
            const descriptionExists = suppliers.elements.some(supplier => supplier.description?.toLowerCase() === description.toLowerCase());
            if(descriptionExists)
                dispatch(openModal({ type: "warning", message: t("SupplierAlreadyExists") }))
            else{
                const supplierParams ={
                    body:{
                        description: description,
                        supplierCode: value.supplierCode?.trim() || null,
                        cnpj: value.cnpj?.trim() || null,
                        contactName: value.contactName?.trim() || null,
                        email: value.email?.trim() || null,
                        phone: value.phone?.trim() || null
                    }
                }
                dispatch(createSupplier(supplierParams)).then((data) => {
                    if(data.error)
                        dispatch(openModal({ type: "warning", message: data.error.message }))
                    else{
                        getListSuppliers(mapPageInfo({...suppliers, ...sortOptions}));
                        closeCreateModal();
                    }
                })
            }    
        }else{
           closeCreateModal()
        }       
    }    

    const handleEditClick = (value, closeEditModal) => {
        const description = value.description?.trim(); 
        if(description){
            const descriptionExists = suppliers.elements.some(supplier => supplier.description?.toLowerCase() === description.toLowerCase());            
            if(descriptionExists && description.toLowerCase() !== suppliers.elements.find(supplier => supplier.supplierId === value.supplierId)?.description?.toLowerCase())
                dispatch(openModal({ type: "warning", message: t("SupplierAlreadyExists") }))
            else{
                const editSupplierParams ={
                    param: value.supplierId,
                    supplierId: value.supplierId,
                    body:{        
                        description: description,
                        supplierCode: value.supplierCode?.trim() || null,
                        cnpj: value.cnpj?.trim() || null,
                        contactName: value.contactName?.trim() || null,
                        email: value.email?.trim() || null,
                        phone: value.phone?.trim() || null
                    } 
                }
                dispatch(editSupplier(editSupplierParams)).then((data) => {
                    if(data.error)
                        dispatch(openModal({ type: "warning", message: data.error.message }))
                    else{
                        getListSuppliers(mapPageInfo({...suppliers, ...sortOptions}));
                        closeEditModal()
                    }    
                })
            }
        }else{
           closeEditModal()
        }             
    }

    const handleDeleteClick = (supplierId, closeDeleteModal) => {
        dispatch(deleteSupplier({param: supplierId})).then((data) => {
            if(data.error)
                dispatch(openModal({ type: "warning", message: data.error.message }))
            else{
                let updatedPageIndex = suppliers.pageIndex;

                if (suppliers.elements.length === 1) {
                    updatedPageIndex = Math.max(0, suppliers.pageIndex - 1);
                }

                const updatedEntryChannels = {
                    ...suppliers,
                    pageIndex: updatedPageIndex,
                };

                getListSuppliers(mapPageInfo({...updatedEntryChannels, ...sortOptions}));
                closeDeleteModal()           
            }
        })  
    }

    function allowDelete(rowData){
        return rowData.data.custom;
    }

    return(
        <div style={{width: "100%"}}>
            <AdminTable
                sectionTitle={t("Suppliers")} 
                masterTitle={t("Supplier")} 
                itemIdField={"supplierId"} 
                itemDescriptionField={"description"} 
                loading={loading} 
                getData={getListSuppliers} 
                dataSource={suppliers} 
                columnasTabla={columnsSuppliers} 
                onCreate={handleSaveNewSupplierClick}
                onDelete={handleDeleteClick}
                onEdit={handleEditClick} 
                numElements={[10, 20, 30]}
                allowDelete={allowDelete}
                sortField={sortOptions}
                setsortField={setSortOptions}
                master={true}
                masterDetail={SupplierServicesDetail}
            />
        </div>
    )
}
export default SupplierManagement;
